import { useQuery, QueryFunctionContext } from '@tanstack/react-query';
import { useLegacyGraphQLRequest } from '@mp/hooks/useGraphQLRequest';
import { GET_SOCIAL_PROOF_MARKET_ACTIVITY } from '@mp/graphql/socialProofMarketActivityQueries';
import { isNumber } from 'lodash';

export type SocialProofVariant = 'no-jobs' | 'low-volume' | 'high-volume';

export type UseSocialProofMarketActivityProps = {
  marketIds: number[];
  positionIds: number[];
  currentUserId: number;
  isMarketplace: boolean;
};

export type UseSocialProofMarketActivityResult = {
  isLoading: boolean;
  error?: unknown;
  variant?: SocialProofVariant;
  shiftsCount?: number;
  minPay?: number;
  maxPay?: number;
  availableJobsCount?: number;
};

const DEFAULTS = {
  DISTANCE: 150,
  JOB_TYPE: 'per_diem',
  NOT_VOID: true,
  LOW_VOLUME_THRESHOLD: 8,
  HIGH_VOLUME_THRESHOLD: 60,
  PAGINATION: { page: 1, perPage: 1 },
};

function buildJobQueryInput(proId: number) {
  const now = new Date().toISOString();
  return {
    search: {
      daily_rate_cents: 0,
      schedule: [] as string[],
      starts_date: now.slice(0, 10),
      starts_time: now,
      job_type: DEFAULTS.JOB_TYPE,
      not_void: DEFAULTS.NOT_VOID,
      status: 'active',
      distance_check: {
        distance: DEFAULTS.DISTANCE,
        account_id: proId,
      },
    },
    ...DEFAULTS.PAGINATION,
  };
}

async function fetchSocialProof(
  { queryKey }: QueryFunctionContext<readonly [string, number[], number[], number]>,
  requestFn: ReturnType<typeof useLegacyGraphQLRequest>['request'],
): Promise<Omit<UseSocialProofMarketActivityResult, 'isLoading'>> {
  const [, marketIds, positionIds, currentUserId] = queryKey;
  const jobsInput = buildJobQueryInput(currentUserId);

  const { socialProofMarketActivity, _availableJobsMeta } = (await requestFn(
    GET_SOCIAL_PROOF_MARKET_ACTIVITY,
    {
      marketIds,
      positionIds,
      availableJobsInput: jobsInput,
    },
  )) as {
    socialProofMarketActivity: {
      shifts_count: number;
      min_pay_amount_cents: number;
      max_pay_amount_cents: number;
    };
    _availableJobsMeta: { count: number };
  };

  const shifts = socialProofMarketActivity.shifts_count;
  const variant: SocialProofVariant =
    shifts === 0
      ? 'no-jobs'
      : shifts < DEFAULTS.LOW_VOLUME_THRESHOLD
      ? 'low-volume'
      : shifts <= DEFAULTS.HIGH_VOLUME_THRESHOLD
      ? 'no-jobs'
      : 'high-volume';

  return {
    variant,
    shiftsCount: shifts,
    minPay: socialProofMarketActivity.min_pay_amount_cents,
    maxPay: socialProofMarketActivity.max_pay_amount_cents,
    availableJobsCount: _availableJobsMeta.count,
  };
}

export function useSocialProofMarketActivity({
  marketIds,
  positionIds,
  currentUserId,
  isMarketplace,
}: UseSocialProofMarketActivityProps): UseSocialProofMarketActivityResult {
  const { request } = useLegacyGraphQLRequest();
  const queryKey = ['socialProofMarketActivity', marketIds, positionIds, currentUserId] as const;

  const enabled =
    marketIds.length > 0 &&
    positionIds.length > 0 &&
    isNumber(currentUserId) &&
    currentUserId > 0 &&
    isMarketplace;

  const { data, error, isLoading } = useQuery(queryKey, (ctx) => fetchSocialProof(ctx, request), {
    enabled,
  });

  return { isLoading, error, ...data };
}

export default useSocialProofMarketActivity;
